<template>
  <div class="space-y-4">
    <!-- Zone de sélection de fichier -->
    <div 
      class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer"
      @click="fileInput?.click()"
      @dragover.prevent
      @drop.prevent="handleFileDrop"
    >
      <input 
        ref="fileInput"
        type="file" 
        accept=".json" 
        class="hidden" 
        @change="handleFileSelect" 
      />
      
      <svg class="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
        <path 
          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" 
          stroke-width="2" 
          stroke-linecap="round" 
          stroke-linejoin="round" 
        />
      </svg>
      
      <p class="mt-2 text-sm text-gray-600">
        <span class="font-medium text-blue-600 hover:text-blue-500">Cliquez pour sélectionner</span>
        ou glissez-déposez un fichier JSON
      </p>
      <p class="text-xs text-gray-500 mt-1">Fichiers .json uniquement (max 10MB)</p>
    </div>
    
    <!-- Affichage du fichier sélectionné -->
    <div v-if="selectedFile" class="bg-green-50 p-4 rounded-md">
      <div class="flex items-start">
        <svg class="h-5 w-5 text-green-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
          <path 
            fill-rule="evenodd" 
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" 
            clip-rule="evenodd" 
          />
        </svg>
        <div class="ml-3 flex-1">
          <p class="text-sm font-medium text-green-800">
            📄 {{ selectedFile.name }}
          </p>
          <p class="text-sm text-green-700">
            {{ formatFileSize(selectedFile.size) }} • {{ formatFileDate(selectedFile.lastModified) }}
          </p>
          
          <!-- Informations supplémentaires du fichier -->
          <div class="mt-2 text-xs text-green-600">
            <span class="inline-flex items-center px-2 py-1 rounded-full bg-green-100">
              ✓ Fichier JSON valide
            </span>
          </div>
        </div>
        
        <!-- Bouton pour changer de fichier -->
        <button 
          class="ml-3 text-green-600 hover:text-green-800 transition-colors"
          title="Changer de fichier"
          @click.stop="clearFile"
        >
          <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
    
    <!-- Bouton d'analyse -->
    <button 
      v-if="selectedFile" 
      :disabled="parsing" 
      class="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white font-medium py-3 px-4 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      @click="analyzeFile"
    >
      <span v-if="parsing" class="flex items-center justify-center">
        <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Analyse en cours...
      </span>
      <span v-else class="flex items-center justify-center">
        🔍 Analyser le fichier
      </span>
    </button>

    <!-- Conseils d'utilisation -->
    <div class="bg-blue-50 border border-blue-200 p-4 rounded-lg">
      <h4 class="font-medium text-blue-900 mb-2">💡 Conseils</h4>
      <ul class="text-sm text-blue-800 space-y-1">
        <li>• Le fichier doit contenir un schéma JSON valide avec les propriétés: appellation, type, section_list</li>
        <li>• Les sections peuvent inclure des traductions et des conditions</li>
        <li>• L'import créera automatiquement les sections parentes manquantes</li>
        <li>• Les conflits avec les données existantes seront analysés et vous pourrez les résoudre</li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { SchemaImportUtils } from '../../../utils/schemaImportUtils';

// Props
const props = defineProps<{
  selectedFile: File | null;
  parsing: boolean;
}>();

// Emits
const emit = defineEmits<{
  'file-selected': [file: File];
  'file-cleared': [];
  'analyze-file': [];
}>();

// Refs
const fileInput = ref<HTMLInputElement | null>(null);

// Gestion de la sélection de fichier
const handleFileSelect = (event: Event): void => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  
  if (file) {
    const validation = SchemaImportUtils.validateSchemaFile(file);
    if (validation.valid) {
      emit('file-selected', file);
    } else {
      alert(validation.error);
      // Réinitialiser l'input
      if (fileInput.value) {
        fileInput.value.value = '';
      }
    }
  }
};

// Gestion du glisser-déposer
const handleFileDrop = (event: DragEvent): void => {
  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    const file = files[0];
    const validation = SchemaImportUtils.validateSchemaFile(file);
    if (validation.valid) {
      emit('file-selected', file);
      // Mettre à jour l'input file pour refléter le fichier sélectionné
      if (fileInput.value) {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        fileInput.value.files = dataTransfer.files;
      }
    } else {
      alert(validation.error);
    }
  }
};

// Effacer le fichier sélectionné
const clearFile = (): void => {
  if (fileInput.value) {
    fileInput.value.value = '';
  }
  emit('file-cleared');
};

// Lancer l'analyse
const analyzeFile = (): void => {
  if (props.selectedFile && !props.parsing) {
    emit('analyze-file');
  }
};

// Formatage
const formatFileSize = (bytes: number): string => {
  return SchemaImportUtils.formatFileSize(bytes);
};

const formatFileDate = (timestamp: number): string => {
  return new Date(timestamp).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};
</script> 